import type React from 'react';
import { useRef, useState } from 'react';

interface IUseConfettiReturnProps<T extends HTMLElement = HTMLDivElement> {
  /** The ref of the confetti container */
  ref: React.RefObject<T>;
  /** The x and y position of the confetti */
  confettiPosition: { x: number; y: number };
  /** Whether the confetti is exploding */
  isExploding: boolean;
  /** Function to start the confetti animation */
  onStartConfetti: () => void;
  /** Function to stop the confetti animation */
  onConfettiComplete: () => void;
}

/**
 * Hook to manage the confetti animation
 * @returns The confetti position, whether it's exploding, and functions to start and stop the animation
 */
function useConfetti<T extends HTMLElement = HTMLDivElement>(): IUseConfettiReturnProps<T> {
  const ref = useRef<T>(null);
  const [confettiPosition, setConfettiPosition] = useState({ x: 0, y: 0 });
  const [isExploding, setIsExploding] = useState(false);

  function updateConfettiPosition(): void {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setConfettiPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    } else {
      // Fallback to center of screen if card ref is not available
      setConfettiPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });
    }
  }

  function handleTriggerConfetti(): void {
    updateConfettiPosition();
    setIsExploding(true);
  }

  function handleOnConfettiComplete(): void {
    setIsExploding(false);
  }

  return {
    ref,
    confettiPosition,
    isExploding,
    onStartConfetti: handleTriggerConfetti,
    onConfettiComplete: handleOnConfettiComplete,
  };
}

export default useConfetti;
